import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useMenuCapability } from '@/lib/auth/permissions';
import { runtimeConfig } from '@/lib/config';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 본사 평면도 — 직원찾기에서 "이 사람 자리가 도면 어디인가" 를 답하는 도면.
 *
 * <h2>기능 전용 창고가 아니라 자료실 폴더다</h2>
 * 도면은 자료실의 표준 경로({@code docs/HQ/80-10/} — 분류 「총무·시설 > 본사평면도」)에 쌓인다.
 * 그래서 자료실 화면에서 보이고, AI 색인기가 읽고(매일 02:00 훑기), 도면 뷰어로도 열린다.
 * 인사팀이 자료실에 올리든 이 화면에서 올리든 <b>같은 폴더</b>다.
 *
 * <h2>현행은 파일명의 적용일이 정한다</h2>
 * {@code 20260901_본사평면도.pdf} 처럼 앞에 날짜를 붙이면 <b>그날부터</b> 그것이 현행이다.
 * 인사이동 도면을 미리 넣어 둘 수 있는 이유이고, "최근에 올린 것" 규칙으로는 못 하는 일이다.
 *
 * <h2>바이트는 same-origin 으로만 읽는다</h2>
 * presigned URL 을 받지 않는다 — 오리진이 스토리지라 CSP·mixed content·CORS 에 걸리고, 만료(기본
 * 600초)가 <b>열어 둔 뷰어</b>를 끊는다. 도면은 열어 놓고 사람을 바꿔 가며 보는 물건이라 특히 아프다.
 */

/** 도면 한 장(= 파일 하나). 층을 파일로 나눠 넣을 수 있어 벌 안에 여러 장이 온다. */
export type FloorplanSheet = {
  /** S3 오브젝트 키. 뷰어가 이 키로 원본을 청한다 — 폴더 밖 키는 서버가 거절한다. */
  key: string;
  name: string;
  /**
   * 이 파일이 담고 있는 층들. <b>도면 글자에서</b> 읽는다 — 사람이 올리는 이름은 대개
   * "본사평면도.pdf" 한 개이고 층은 도면 안에 "지상 3층 평면도" 로 적혀 있다. 스캔본이라 못 읽으면
   * 파일명에서 한 번 더 시도하고, 그래도 모르면 빈 목록이다.
   */
  floors: string[];
};

/** 도면에서 찾은 한 사람의 자리. 명함이 도면을 열지 않고 "도면상 3층" 을 말하는 데 쓴다. */
export type FloorplanLocation = {
  /** 단정할 수 있을 때의 층. 층이 갈리거나 못 찾으면 null. */
  floor: string | null;
  /** 서로 다른 층에서 발견됐는가(동명이인·옛 자리). true 면 화면은 단정하지 않는다. */
  ambiguous: boolean;
  hits: Array<{ sheetKey: string; sheetName: string; page: number; floor: string | null }>;
};

export type Floorplan = {
  available: boolean;
  /** 도면을 넣어 두는 폴더. 화면이 "여기에 넣으세요" 라고 말할 수 있어야 한다. */
  prefix: string;
  /** 지금 보고 있는 벌의 적용일(`yyyy-MM-dd`). 파일명에 날짜가 없으면 null. */
  effectiveDate: string | null;
  /** 미리 넣어 둔 다음 도면의 적용일. 있으면 화면이 "○일부터 바뀝니다" 라고 말한다. */
  pendingDate: string | null;
  sheets: FloorplanSheet[];
};

/** 뷰어가 원본을 읽는 주소. */
export function floorplanFileUrl(key: string): string {
  return `${runtimeConfig().apiBaseUrl}/v1/directory/floorplan/file?key=${encodeURIComponent(key)}`;
}

/**
 * 지금 유효한 도면 한 벌. 명함마다 묻게 되므로 오래 신선한 것으로 본다 — 하루에 몇 번 바뀌는
 * 자료가 아니다. 날짜가 넘어가며 현행이 바뀌는 것은 다음 조회 때 따라온다.
 */
export function useFloorplan(enabled = true) {
  return useQuery({
    queryKey: queryKeys.directoryFloorplan,
    queryFn: () => apiFetch<Floorplan>('/v1/directory/floorplan'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * 이 사람이 도면 어디에 있는가 — <b>서버가 읽어 캐시한 결과</b>를 받는다.
 *
 * <p>브라우저도 같은 글자층을 읽을 수 있고 뷰어는 실제로 그렇게 한다. 그러나 명함은 도면을 열지
 * 않은 채로 층을 말해야 하고, 그러자고 카드를 열 때마다 몇 MB 짜리 도면을 받아 파싱할 수는 없다.
 * 서버가 한 번 읽으면 전 직원이 그 결과를 나눠 쓴다({@code FloorplanTextReader}).
 */
export function useFloorplanLocation(name: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.directoryFloorplan, 'locate', name],
    queryFn: () => apiFetch<FloorplanLocation>(`/v1/directory/floorplan/locate?name=${encodeURIComponent(name)}`),
    enabled: enabled && name.trim() !== '',
    staleTime: 5 * 60_000,
  });
}

/**
 * 도면을 올리고 내릴 수 있는가. <b>인가가 아니다</b> — 버튼을 띄울지의 판단이고, 서버가 매 요청
 * 같은 판정을 다시 한다({@code DirectoryController.FLOORPLAN_WRITE_MENUS}). 자리위치(층·구역·번호)를
 * 입력하는 사람과 그 자리가 그려진 도면을 관리하는 사람은 같은 사람이라 인력 편집과 같은 문을 쓴다.
 */
export function useCanManageFloorplan(): boolean {
  const byManagement = useMenuCapability('hr_management', 'create');
  const byRecords = useMenuCapability('hr_records', 'create');
  return byManagement || byRecords;
}

/**
 * 도면 올리기 — presigned PUT 으로 스토리지에 직접 올린다(급여명세서·결재첨부와 같은 규칙).
 * <b>저장하는 단계가 따로 없다</b>: 파일이 폴더에 놓이는 순간 그것이 자료다. 대신 목록을 다시 읽어
 * 서버가 계산한 현행(적용일이 미래면 아직 안 바뀐다)을 그대로 받는다.
 */
export function useUploadFloorplan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, effectiveDate }: { file: File; effectiveDate: string | null }): Promise<Floorplan> => {
      const { url } = await apiFetch<{ key: string; url: string }>('/v1/directory/floorplan/uploads', {
        method: 'POST',
        body: { fileName: file.name, effectiveDate },
      });

      const put = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/pdf' },
      });
      if (!put.ok) throw new Error('도면을 저장소에 올리지 못했습니다.');

      return apiFetch<Floorplan>('/v1/directory/floorplan');
    },
    onSuccess: (data) => {
      client.setQueryData(queryKeys.directoryFloorplan, data);
    },
  });
}

/** 도면 한 장 내리기. 폴더 밖 키는 서버가 거절한다. */
export function useDeleteFloorplanSheet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      apiFetch<Floorplan>(`/v1/directory/floorplan?key=${encodeURIComponent(key)}`, { method: 'DELETE' }),
    onSuccess: (data) => {
      client.setQueryData(queryKeys.directoryFloorplan, data);
    },
  });
}
